import { InputState, Vector2 } from '../types/game.types';

export interface TouchControl {
  type: 'button' | 'joystick' | 'swipe';
  bounds: { x: number; y: number; width: number; height: number };
  action: string;
  active?: boolean;
}

export class MobileInputHandler {
  private static instance: MobileInputHandler | null = null;
  private virtualKeys: { [key: string]: boolean } = {};
  private touchControls: TouchControl[] = [];
  private activeTouch: Touch | null = null;
  private swipeStart: Vector2 | null = null;
  private isMobile: boolean = false;
  private canvas: HTMLCanvasElement | null = null;
  
  private constructor() {
    this.detectMobile();
  }
  
  public static getInstance(): MobileInputHandler {
    if (!MobileInputHandler.instance) {
      MobileInputHandler.instance = new MobileInputHandler();
    }
    return MobileInputHandler.instance;
  }
  
  private detectMobile(): void {
    // Detect if device is mobile/tablet
    this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                    ('ontouchstart' in window) ||
                    (navigator.maxTouchPoints > 0);
  }
  
  public getIsMobile(): boolean {
    return this.isMobile;
  }

  public setCanvas(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
  }
  
  public setupGameControls(gameType: string): void {
    this.touchControls = [];
    this.virtualKeys = {};
    
    switch (gameType) {
      case 'breakout':
        this.setupBreakoutControls();
        break;
      case 'galactic-defense':
        this.setupGalacticDefenseControls();
        break;
      case 'maze-runner':
        this.setupMazeRunnerControls();
        break;
      case 'river-crossing':
        this.setupRiverCrossingControls();
        break;
      case 'block-drop':
        this.setupBlockDropControls();
        break;
      case 'tunnel-blaster':
        this.setupTunnelBlasterControls();
        break;
      case 'space-debris': // Asteroids
        this.setupAsteroidsControls();
        break;
      case 'centipede':
        this.setupCentipedeControls();
        break;
      case 'defender':
        this.setupDefenderControls();
        break;
    }
  }
  
  private setupBreakoutControls(): void {
    // For Breakout, we'll use touch position for paddle control
    // and tap to launch ball
  }
  
  private setupGalacticDefenseControls(): void {
    // Left/right buttons and fire button
    const screenHeight = window.innerHeight;
    const screenWidth = window.innerWidth;
    const buttonSize = 60;
    const margin = 20;
    
    this.touchControls = [
      {
        type: 'button',
        bounds: { x: margin, y: screenHeight - buttonSize - margin, width: buttonSize, height: buttonSize },
        action: 'ArrowLeft'
      },
      {
        type: 'button',
        bounds: { x: margin + buttonSize + 10, y: screenHeight - buttonSize - margin, width: buttonSize, height: buttonSize },
        action: 'ArrowRight'
      },
      {
        type: 'button',
        bounds: { x: screenWidth - buttonSize - margin, y: screenHeight - buttonSize - margin, width: buttonSize, height: buttonSize },
        action: 'Space'
      }
    ];
  }
  
  private setupMazeRunnerControls(): void {
    // Swipe gestures for movement
    this.touchControls = [
      {
        type: 'swipe',
        bounds: { x: 0, y: 0, width: window.innerWidth, height: window.innerHeight },
        action: 'swipe'
      }
    ];
  }
  
  private setupRiverCrossingControls(): void {
    // D-pad style controls
    const screenHeight = window.innerHeight;
    const buttonSize = 50;
    const margin = 20;
    const dpadX = margin;
    const dpadY = screenHeight - (buttonSize * 3) - margin;
    
    this.touchControls = [
      {
        type: 'button',
        bounds: { x: dpadX + buttonSize, y: dpadY, width: buttonSize, height: buttonSize },
        action: 'ArrowUp'
      },
      {
        type: 'button',
        bounds: { x: dpadX + buttonSize, y: dpadY + buttonSize * 2, width: buttonSize, height: buttonSize },
        action: 'ArrowDown'
      },
      {
        type: 'button',
        bounds: { x: dpadX, y: dpadY + buttonSize, width: buttonSize, height: buttonSize },
        action: 'ArrowLeft'
      },
      {
        type: 'button',
        bounds: { x: dpadX + buttonSize * 2, y: dpadY + buttonSize, width: buttonSize, height: buttonSize },
        action: 'ArrowRight'
      }
    ];
  }
  
  private setupBlockDropControls(): void {
    // D-pad for movement, buttons for rotate and hard drop
    // Block Drop is portrait mode: 600x900
    const canvasHeight = this.canvas?.height || 900;
    const canvasWidth = this.canvas?.width || 600;
    const buttonSize = 50;
    const margin = 10;

    this.touchControls = [
      // Left arrow
      {
        type: 'button',
        bounds: { x: margin, y: canvasHeight - buttonSize - margin, width: buttonSize, height: buttonSize },
        action: 'ArrowLeft'
      },
      // Right arrow
      {
        type: 'button',
        bounds: { x: margin + buttonSize + 10, y: canvasHeight - buttonSize - margin, width: buttonSize, height: buttonSize },
        action: 'ArrowRight'
      },
      // Down (soft drop)
      {
        type: 'button',
        bounds: { x: margin + buttonSize / 2 + 5, y: canvasHeight - buttonSize * 2 - margin - 10, width: buttonSize, height: buttonSize },
        action: 'ArrowDown'
      },
      // Rotate (right side)
      {
        type: 'button',
        bounds: { x: canvasWidth - buttonSize - margin, y: canvasHeight - buttonSize - margin, width: buttonSize, height: buttonSize },
        action: 'ArrowUp'
      },
      // Hard drop / Space (right side, above rotate)
      {
        type: 'button',
        bounds: { x: canvasWidth - buttonSize - margin, y: canvasHeight - buttonSize * 2 - margin - 10, width: buttonSize, height: buttonSize },
        action: 'Space'
      }
    ];
  }
  
  private setupTunnelBlasterControls(): void {
    // Touch position on tunnel + tap to fire
  }
  
  private setupAsteroidsControls(): void {
    // Virtual joystick + buttons
    const screenHeight = window.innerHeight;
    const screenWidth = window.innerWidth;
    const buttonSize = 50;
    const margin = 20;
    
    this.touchControls = [
      {
        type: 'button',
        bounds: { x: margin, y: screenHeight - buttonSize * 2 - margin, width: buttonSize, height: buttonSize },
        action: 'ArrowLeft'
      },
      {
        type: 'button',
        bounds: { x: margin + buttonSize + 10, y: screenHeight - buttonSize * 2 - margin, width: buttonSize, height: buttonSize },
        action: 'ArrowRight'
      },
      {
        type: 'button',
        bounds: { x: margin + buttonSize / 2 + 5, y: screenHeight - buttonSize * 3 - margin, width: buttonSize, height: buttonSize },
        action: 'ArrowUp'
      },
      {
        type: 'button',
        bounds: { x: screenWidth - buttonSize - margin, y: screenHeight - buttonSize - margin, width: buttonSize, height: buttonSize },
        action: 'Space'
      },
      {
        type: 'button',
        bounds: { x: screenWidth - buttonSize * 2 - margin - 10, y: screenHeight - buttonSize - margin, width: buttonSize, height: buttonSize },
        action: 'KeyH'
      }
    ];
  }
  
  private setupCentipedeControls(): void {
    // Virtual joystick + fire button
    const screenHeight = window.innerHeight;
    const screenWidth = window.innerWidth;
    const buttonSize = 60;
    const margin = 20;
    
    this.touchControls = [
      {
        type: 'joystick',
        bounds: { x: margin, y: screenHeight - 120 - margin, width: 120, height: 120 },
        action: 'movement'
      },
      {
        type: 'button',
        bounds: { x: screenWidth - buttonSize - margin, y: screenHeight - buttonSize - margin, width: buttonSize, height: buttonSize },
        action: 'Space'
      }
    ];
  }
  
  private setupDefenderControls(): void {
    // Complex control scheme
    const screenHeight = window.innerHeight;
    const screenWidth = window.innerWidth;
    const buttonSize = 45;
    const margin = 15;

    this.touchControls = [
      {
        type: 'button',
        bounds: { x: margin, y: screenHeight - buttonSize - margin, width: buttonSize, height: buttonSize },
        action: 'ArrowLeft'
      },
      {
        type: 'button',
        bounds: { x: margin + buttonSize + 5, y: screenHeight - buttonSize - margin, width: buttonSize, height: buttonSize },
        action: 'ArrowRight'
      },
      {
        type: 'button',
        bounds: { x: margin, y: screenHeight - buttonSize * 2 - margin - 5, width: buttonSize, height: buttonSize },
        action: 'ArrowUp'
      },
      {
        type: 'button',
        bounds: { x: margin + buttonSize + 5, y: screenHeight - buttonSize * 2 - margin - 5, width: buttonSize, height: buttonSize },
        action: 'ArrowDown'
      },
      {
        type: 'button',
        bounds: { x: screenWidth - buttonSize - margin, y: screenHeight - buttonSize - margin, width: buttonSize, height: buttonSize },
        action: 'Space'
      },
      {
        type: 'button',
        bounds: { x: screenWidth - buttonSize * 2 - margin - 5, y: screenHeight - buttonSize - margin, width: buttonSize, height: buttonSize },
        action: 'KeyB'
      },
      {
        type: 'button',
        bounds: { x: screenWidth - buttonSize - margin, y: screenHeight - buttonSize * 2 - margin - 5, width: buttonSize, height: buttonSize },
        action: 'KeyH'
      }
    ];
  }

  public processTouchInput(input: InputState): InputState {
    if (!this.isMobile) return input;
    
    // Merge virtual keys with actual keys
    const mergedInput = { ...input };
    Object.keys(this.virtualKeys).forEach(key => {
      if (this.virtualKeys[key]) {
        mergedInput.keys[key] = true;
      }
    });
    
    return mergedInput;
  }
  
  public handleTouchStart(touch: Touch): void {
    this.activeTouch = touch;
    this.swipeStart = { x: touch.clientX, y: touch.clientY };

    // Convert touch coordinates to canvas coordinates
    const canvasPoint = this.screenToCanvasCoordinates(touch.clientX, touch.clientY);

    // Check button presses
    this.touchControls.forEach(control => {
      if (control.type === 'button' && this.isPointInBounds(canvasPoint.x, canvasPoint.y, control.bounds)) {
        this.virtualKeys[control.action] = true;
        control.active = true;
      }
    });
  }
  
  public handleTouchMove(touch: Touch): void {
    if (!this.swipeStart) return;
    
    const deltaX = touch.clientX - this.swipeStart.x;
    const deltaY = touch.clientY - this.swipeStart.y;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    
    if (distance > 30) {
      // Detect swipe direction
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        if (deltaX > 0) {
          this.virtualKeys['ArrowRight'] = true;
          this.virtualKeys['ArrowLeft'] = false;
        } else {
          this.virtualKeys['ArrowLeft'] = true;
          this.virtualKeys['ArrowRight'] = false;
        }
      } else {
        if (deltaY > 0) {
          this.virtualKeys['ArrowDown'] = true;
          this.virtualKeys['ArrowUp'] = false;
        } else {
          this.virtualKeys['ArrowUp'] = true;
          this.virtualKeys['ArrowDown'] = false;
        }
      }
    }
    
    // Handle joystick movement
    this.touchControls.forEach(control => {
      if (control.type === 'joystick' && control.active) {
        const centerX = control.bounds.x + control.bounds.width / 2;
        const centerY = control.bounds.y + control.bounds.height / 2;
        const dx = touch.clientX - centerX;
        const dy = touch.clientY - centerY;
        const angle = Math.atan2(dy, dx);
        const dist = Math.min(Math.sqrt(dx * dx + dy * dy), control.bounds.width / 2);
        
        // Convert to directional keys
        if (dist > 10) {
          const angleInDegrees = (angle * 180 / Math.PI + 360) % 360;
          
          this.virtualKeys['ArrowRight'] = angleInDegrees < 45 || angleInDegrees > 315;
          this.virtualKeys['ArrowDown'] = angleInDegrees > 45 && angleInDegrees < 135;
          this.virtualKeys['ArrowLeft'] = angleInDegrees > 135 && angleInDegrees < 225;
          this.virtualKeys['ArrowUp'] = angleInDegrees > 225 && angleInDegrees < 315;
        } else {
          this.virtualKeys['ArrowRight'] = false;
          this.virtualKeys['ArrowDown'] = false;
          this.virtualKeys['ArrowLeft'] = false;
          this.virtualKeys['ArrowUp'] = false;
        }
      }
    });
  }
  
  public handleTouchEnd(): void {
    this.activeTouch = null;
    this.swipeStart = null;
    
    // Clear virtual keys
    Object.keys(this.virtualKeys).forEach(key => {
      this.virtualKeys[key] = false;
    });
    
    // Deactivate controls
    this.touchControls.forEach(control => {
      control.active = false;
    });
  }
  
  private screenToCanvasCoordinates(screenX: number, screenY: number): { x: number, y: number } {
    if (!this.canvas) return { x: screenX, y: screenY };

    const rect = this.canvas.getBoundingClientRect();
    const scaleX = this.canvas.width / rect.width;
    const scaleY = this.canvas.height / rect.height;

    return {
      x: (screenX - rect.left) * scaleX,
      y: (screenY - rect.top) * scaleY
    };
  }

  private isPointInBounds(x: number, y: number, bounds: { x: number; y: number; width: number; height: number }): boolean {
    return x >= bounds.x && x <= bounds.x + bounds.width &&
           y >= bounds.y && y <= bounds.y + bounds.height;
  }
  
  public renderControls(ctx: CanvasRenderingContext2D): void {
    if (!this.isMobile) return;
    
    ctx.save();
    
    this.touchControls.forEach(control => {
      if (control.type === 'button') {
        // Draw button
        ctx.fillStyle = control.active ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.5)';
        ctx.fillRect(control.bounds.x, control.bounds.y, control.bounds.width, control.bounds.height);
        
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = 2;
        ctx.strokeRect(control.bounds.x, control.bounds.y, control.bounds.width, control.bounds.height);
        
        // Draw label
        ctx.fillStyle = '#FFFFFF';
        ctx.font = '16px monospace';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        let label = '';
        switch (control.action) {
          case 'ArrowLeft': label = '←'; break;
          case 'ArrowRight': label = '→'; break;
          case 'ArrowUp': label = '↑'; break;
          case 'ArrowDown': label = '↓'; break;
          case 'Space': label = 'FIRE'; break;
          case 'KeyB': label = 'BOMB'; break;
          case 'KeyH': label = 'HYPER'; break;
          default: label = control.action.replace('Key', '');
        }
        
        ctx.fillText(
          label,
          control.bounds.x + control.bounds.width / 2,
          control.bounds.y + control.bounds.height / 2
        );
      } else if (control.type === 'joystick') {
        // Draw joystick base
        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        ctx.beginPath();
        ctx.arc(
          control.bounds.x + control.bounds.width / 2,
          control.bounds.y + control.bounds.height / 2,
          control.bounds.width / 2,
          0,
          Math.PI * 2
        );
        ctx.fill();
        
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = 2;
        ctx.stroke();
        
        // Draw joystick knob if active
        if (control.active && this.activeTouch) {
          const centerX = control.bounds.x + control.bounds.width / 2;
          const centerY = control.bounds.y + control.bounds.height / 2;
          const dx = this.activeTouch.clientX - centerX;
          const dy = this.activeTouch.clientY - centerY;
          const dist = Math.min(Math.sqrt(dx * dx + dy * dy), control.bounds.width / 2 - 10);
          const angle = Math.atan2(dy, dx);
          
          ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
          ctx.beginPath();
          ctx.arc(
            centerX + Math.cos(angle) * dist,
            centerY + Math.sin(angle) * dist,
            15,
            0,
            Math.PI * 2
          );
          ctx.fill();
        }
      }
    });
    
    ctx.restore();
  }
}